// pattern: Imperative Shell
// Hash-based client-side router

export type Route =
  | { readonly type: 'landing' }
  | { readonly type: 'dashboard'; readonly dashboardId: string }
  | { readonly type: 'upload'; readonly dashboardId: string }
  | { readonly type: 'editor'; readonly dashboardId: string; readonly quizId: string }
  | { readonly type: 'quiz'; readonly quizId: string }
  | { readonly type: 'not-found' };

/** Parse window.location.hash into a Route */
export function parseHash(hash: string): Route {
  // Remove leading # and optional /
  const path = hash.replace(/^#\/?/, '');

  if (!path || path === '/') {
    return { type: 'landing' };
  }

  // #/t/{dashboardId}/q/{quizId} — teacher quiz editor
  const editorMatch = path.match(/^t\/([A-Za-z0-9]{8})\/q\/([A-Za-z0-9]{8})$/);
  if (editorMatch) {
    return { type: 'editor', dashboardId: editorMatch[1]!, quizId: editorMatch[2]! };
  }

  // #/t/{dashboardId}/new — teacher upload new quiz
  const uploadMatch = path.match(/^t\/([A-Za-z0-9]{8})\/new$/);
  if (uploadMatch) {
    return { type: 'upload', dashboardId: uploadMatch[1]! };
  }

  // #/t/{dashboardId} — teacher dashboard
  const dashboardMatch = path.match(/^t\/([A-Za-z0-9]{8})$/);
  if (dashboardMatch) {
    return { type: 'dashboard', dashboardId: dashboardMatch[1]! };
  }

  // #/q/{quizId} — student quiz
  const quizMatch = path.match(/^q\/([A-Za-z0-9]{8})$/);
  if (quizMatch) {
    return { type: 'quiz', quizId: quizMatch[1]! };
  }

  return { type: 'not-found' };
}

/**
 * Navigate to a hash route.
 * Note: Browser automatically prefixes with '#', so pass the full hash (e.g., '#/t/abc123').
 * Prefer using `routeToHash()` to generate compliant hash strings from Route objects.
 */
export function navigate(hash: string): void {
  window.location.hash = hash;
}

/** Build a hash URL for a route */
export function routeToHash(route: Route): string {
  switch (route.type) {
    case 'landing':
      return '#/';
    case 'dashboard':
      return `#/t/${route.dashboardId}`;
    case 'upload':
      return `#/t/${route.dashboardId}/new`;
    case 'editor':
      return `#/t/${route.dashboardId}/q/${route.quizId}`;
    case 'quiz':
      return `#/q/${route.quizId}`;
    case 'not-found':
      return '#/';
  }
}
